const ANALYSIS_UPDATE_EVENT = 'fc:analysisupdate';
const ANALYSIS_CLEAR_EVENT = 'fc:analysisclear';
const ANALYSIS_ERROR_EVENT = 'fc:analysiserror';

export class FelixChessEvalBar {
    constructor(options = {}) {
        this.rootEl = options.rootEl || null;
        this.evalBarEl = options.evalBarEl || null;

        this.orientation = options.orientation || 'vertical';
        this.showScore = options.showScore !== false;
        // scorePerspective is kept for API compatibility but the bar always
        // displays from White's absolute perspective, which is the standard
        // chess convention. Stockfish UCI scores are side-to-move relative, so
        // we normalise them ourselves (see normalizeScore / toWhitePercent).
        this.scorePerspective = options.scorePerspective || 'white';
        this.isDestroyed = false;

        this.currentPercentWhite = 50;
        this.currentScoreDisplay = '0.00';

        this._boundHandleAnalysisUpdate = this.handleAnalysisUpdate.bind(this);
        this._boundHandleAnalysisClear = this.handleAnalysisClear.bind(this);
        this._boundHandleAnalysisError = this.handleAnalysisError.bind(this);

        this.renderShell();
        this.bindAnalyzerEvents();
        this.updateBar(50, '0.00');
        this.hide();
    }

    renderShell() {
        if (!this.evalBarEl) {
            return;
        }

        this.evalBarEl.innerHTML = `
            <div class="fc-boardViewer__evalBarInner" data-role="evalBarInner">
                <div class="fc-boardViewer__evalBarBlack" data-role="evalBarBlack"></div>
                <div class="fc-boardViewer__evalBarWhite" data-role="evalBarWhite"></div>
                <div class="fc-boardViewer__evalBarScore fc-boardViewer__evalBarScore--top" data-role="evalBarScoreTop"></div>
                <div class="fc-boardViewer__evalBarScore fc-boardViewer__evalBarScore--bottom" data-role="evalBarScoreBottom"></div>
            </div>
        `;
    }

    bindAnalyzerEvents() {
        if (!this.rootEl) {
            return;
        }

        this.rootEl.addEventListener(ANALYSIS_UPDATE_EVENT, this._boundHandleAnalysisUpdate);
        this.rootEl.addEventListener(ANALYSIS_CLEAR_EVENT, this._boundHandleAnalysisClear);
        this.rootEl.addEventListener(ANALYSIS_ERROR_EVENT, this._boundHandleAnalysisError);
    }

    handleAnalysisUpdate(event) {
        if (this.isDestroyed) {
            return;
        }

        const detail = event?.detail || {};
        const bestLine = detail.bestLine || null;

        if (!bestLine) {
            this.show();
            this.updateBar(50, '0.00');
            return;
        }

        // Pass the full detail so toWhitePercent / toDisplayScore can read the FEN.
        const percentWhite = this.toWhitePercent(bestLine, detail);
        const scoreDisplay = this.toDisplayScore(bestLine, detail);

        this.show();
        this.updateBar(percentWhite, scoreDisplay);
    }

    handleAnalysisClear() {
        if (this.isDestroyed) {
            return;
        }

        this.updateBar(50, '0.00');
        this.hide();
    }

    handleAnalysisError() {
        if (this.isDestroyed) {
            return;
        }

        this.hide();
    }

    updateBar(percentWhite, scoreDisplay) {
        if (!this.evalBarEl) {
            return;
        }

        const whitePct = this.clamp(percentWhite, 0, 100);
        const blackPct = 100 - whitePct;

        this.currentPercentWhite = whitePct;
        this.currentScoreDisplay = scoreDisplay || '0.00';

        const whiteEl = this.evalBarEl.querySelector('[data-role="evalBarWhite"]');
        const blackEl = this.evalBarEl.querySelector('[data-role="evalBarBlack"]');
        const topScoreEl = this.evalBarEl.querySelector('[data-role="evalBarScoreTop"]');
        const bottomScoreEl = this.evalBarEl.querySelector('[data-role="evalBarScoreBottom"]');

        if (this.orientation === 'vertical') {
            if (blackEl) {
                blackEl.style.height = `${blackPct}%`;
            }
            if (whiteEl) {
                whiteEl.style.height = `${whitePct}%`;
            }
        } else {
            if (blackEl) {
                blackEl.style.width = `${blackPct}%`;
            }
            if (whiteEl) {
                whiteEl.style.width = `${whitePct}%`;
            }
        }

        if (this.showScore) {
            const whiteBetter = whitePct >= 50;
            const topText = whiteBetter ? '' : scoreDisplay;
            const bottomText = whiteBetter ? scoreDisplay : '';

            if (topScoreEl) {
                topScoreEl.textContent = topText;
                topScoreEl.hidden = !topText;
            }

            if (bottomScoreEl) {
                bottomScoreEl.textContent = bottomText;
                bottomScoreEl.hidden = !bottomText;
            }
        } else {
            if (topScoreEl) topScoreEl.hidden = true;
            if (bottomScoreEl) bottomScoreEl.hidden = true;
        }

        this.evalBarEl.setAttribute('data-white-percent', String(Math.round(whitePct)));
        this.evalBarEl.setAttribute('data-score', scoreDisplay || '');
    }

    /**
     * Convert a raw Stockfish score (always from the side-to-move perspective
     * per the UCI protocol) to an absolute White-relative centipawn value.
     *
     * When it is Black's turn, Stockfish's +100 cp means Black is a pawn up,
     * which corresponds to -100 cp from White's point of view.  We therefore
     * negate the score whenever the FEN shows it is Black's turn.
     *
     * This normalisation is applied unconditionally — it is not gated on
     * scorePerspective — because the raw engine output is always side-to-move
     * relative regardless of any display preference.
     */
    normalizeScore(rawScore, fen) {
        const turn = this.extractTurnFromFen(fen);
        return turn === 'b' ? -rawScore : rawScore;
    }

    toWhitePercent(bestLine = {}, analysisDetail = {}) {
        const scoreType = bestLine.scoreType || null;
        const rawScore = Number.isFinite(bestLine.score) ? bestLine.score : 0;

        // Convert side-to-move score → White-absolute score.
        const fen = analysisDetail.fen || '';
        const score = this.normalizeScore(rawScore, fen);

        if (scoreType === 'mate') {
            if (score === 0) {
                return 50;
            }
            return score > 0 ? 99 : 1;
        }

        if (scoreType !== 'cp') {
            return 50;
        }

        /*
         * Smooth bounded mapping for centipawns.
         * Keeps 0.00 at 50/50, grows smoothly, and avoids absurd extremes.
         * Examples:
         *   0 cp   -> 50%
         *   100 cp -> ~68%
         *   300 cp -> ~89%
         *   600 cp -> ~97%
         */
        const normalized = Math.tanh(score / 250);
        return 50 + (normalized * 47);
    }

    /**
     * Format the score for display on the eval bar label, normalised to
     * White's perspective (positive = White is better).
     *
     * analysisDetail must carry the `fen` field so we can detect whose turn it
     * is and apply the side-to-move → White conversion.
     */
    toDisplayScore(bestLine = {}, analysisDetail = {}) {
        const fen = analysisDetail?.fen || '';

        if (bestLine.scoreType === 'mate' && Number.isFinite(bestLine.score)) {
            // Normalise mate score to White's perspective.
            const rawMate = bestLine.score;
            const mate = this.normalizeScore(rawMate, fen);
            if (mate === 0) {
                return 'M0';
            }
            return mate > 0 ? `#${mate}` : `-#${Math.abs(mate)}`;
        }

        if (bestLine.scoreType === 'cp' && Number.isFinite(bestLine.score)) {
            const rawCp = bestLine.score;
            const cp = this.normalizeScore(rawCp, fen);
            const pawns = cp / 100;
            return `${pawns < 0 ? '-' : ''}${Math.abs(pawns).toFixed(2)}`;
        }

        // Fallback: if the engine already provided a formatted string and we
        // have no structured data to normalise, return it as-is.
        if (typeof bestLine.scoreDisplay === 'string' && bestLine.scoreDisplay.trim()) {
            return bestLine.scoreDisplay.trim();
        }

        return '0.00';
    }

    extractTurnFromFen(fen = '') {
        const parts = String(fen || '').trim().split(/\s+/);
        return parts.length >= 2 ? parts[1] : 'w';
    }

    clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    }

    show() {
        if (this.evalBarEl) {
            this.evalBarEl.hidden = false;
        }
    }

    hide() {
        if (this.evalBarEl) {
            this.evalBarEl.hidden = true;
        }
    }

    clear() {
        this.updateBar(50, '0.00');
        this.hide();
    }

    destroy() {
        if (this.isDestroyed) {
            return;
        }

        this.isDestroyed = true;

        if (this.rootEl) {
            this.rootEl.removeEventListener(ANALYSIS_UPDATE_EVENT, this._boundHandleAnalysisUpdate);
            this.rootEl.removeEventListener(ANALYSIS_CLEAR_EVENT, this._boundHandleAnalysisClear);
            this.rootEl.removeEventListener(ANALYSIS_ERROR_EVENT, this._boundHandleAnalysisError);
        }

        this.clear();
    }
}